export class ColorHelper {
    public static ParseHexToRgbaString(hex: string): string {
        const color = ColorHelper._parseHexToRgba(hex);
        return `rgba(${color.r},${color.g},${color.b},${color.a})`;
    }

    public static ParseColorToHexa(color: string): string {
        let r: number;
        let g: number;
        let b: number;
        let a: number;

        if (color.startsWith('#')) {
            if (color.length == 7 || color.length == 9) {
                return color;
            }
            else {
                throw Error('Wrong hex color');
            }

        }
        else if (color.startsWith('rgb')) {
            const rgba = ColorHelper.ParseColorToRgba(color);

            return ColorHelper._rgbToHex(rgba.r, rgba.g, rgba.b, rgba.a * 255);
        }

        return ColorHelper._parseColorNameToHex(color);
    }

    public static ParseColorToRgba(color: string): { r: number, g: number, b: number, a: number } {
        if (color.startsWith('#')) {
            return ColorHelper._parseHexToRgba(color);
        }
        else if (!color.startsWith('rgb')) {
            return ColorHelper._parseHexToRgba(ColorHelper._parseColorNameToHex(color));
        }

        const [r, g, b, a] = color
            .slice(color.indexOf('(') + 1, -1)
            .split(',');

        return {
            r: Number.parseInt(r),
            g: Number.parseInt(g),
            b: Number.parseInt(b),
            a: Number.parseFloat(a) ?? 1
        };
    }

    /**
     * Check whether the given background color is "light" or "dark", to determine whether
     * the foreground color should be black or white accordingly.
     *
     * Implemenation of this W3C's algorithm: https://www.w3.org/TR/AERT/#color-contrast
     * @param color
     */
    public static IsLightBackgroundColor(color: string): boolean {
        const rgba = ColorHelper.ParseColorToRgba(color);

        const brightness = (rgba.r * 299 + rgba.g * 587 + rgba.b * 114) / 1000;

        return brightness > 125 || rgba.a < 0.5;
    }

    private static _rgbToHex(r: number, g: number, b: number, a: number) {
        const rHex = ((r & 0xff) + 0x100).toString(16).slice(1);
        const gHex = ((g & 0xff) + 0x100).toString(16).slice(1);
        const bHex = ((b & 0xff) + 0x100).toString(16).slice(1);
        const aHex = ((a & 0xff) + 0x100).toString(16).slice(1);

        return `#${rHex}${gHex}${bHex}${aHex}`.toUpperCase();
    }

    private static _parseHexToRgba(hex: string): { r: number, g: number, b: number, a: number } {
        // Expand shorthand form (e.g. '03FE') to full form (e.g. '0033FFEE')
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b, a) => {
            return r + r + g + g + b + b + a + a;
        });

        const result = /^#?(?<r>[a-f\d]{2})(?<g>[a-f\d]{2})(?<b>[a-f\d]{2})(?<a>[a-f\d]{2})?$/i.exec(hex);

        return result ? {
            r: parseInt(result.groups.r, 16),
            g: parseInt(result.groups.g, 16),
            b: parseInt(result.groups.b, 16),
            a: parseInt(result.groups.a ?? 'ff', 16) / 255
        } : null;
    }

    private static _parseColorNameToHex(colorName: string): string {
        const hexColor = ColorHelper.colors[colorName.toLowerCase()];

        return hexColor
            ? `${hexColor}ff`
            : '#000000ff';
    }

    static readonly colors = {
        'aliceblue': '#f0f8ff',
        'antiquewhite': '#faebd7',
        'aqua': '#00ffff',
        'aquamarine': '#7fffd4',
        'azure': '#f0ffff',
        'beige': '#f5f5dc',
        'bisque': '#ffe4c4',
        'black': '#000000',
        'blanchedalmond': '#ffebcd',
        'blue': '#0000ff',
        'blueviolet': '#8a2be2',
        'brown': '#a52a2a',
        'burlywood': '#deb887',
        'cadetblue': '#5f9ea0',
        'chartreuse': '#7fff00',
        'chocolate': '#d2691e',
        'coral': '#ff7f50',
        'cornflowerblue': '#6495ed',
        'cornsilk': '#fff8dc',
        'crimson': '#dc143c',
        'cyan': '#00ffff',
        'darkblue': '#00008b',
        'darkcyan': '#008b8b',
        'darkgoldenrod': '#b8860b',
        'darkgray': '#a9a9a9',
        'darkgreen': '#006400',
        'darkkhaki': '#bdb76b',
        'darkmagenta': '#8b008b',
        'darkolivegreen': '#556b2f',
        'darkorange': '#ff8c00',
        'darkorchid': '#9932cc',
        'darkred': '#8b0000',
        'darksalmon': '#e9967a',
        'darkseagreen': '#8fbc8f',
        'darkslateblue': '#483d8b',
        'darkslategray': '#2f4f4f',
        'darkturquoise': '#00ced1',
        'darkviolet': '#9400d3',
        'deeppink': '#ff1493',
        'deepskyblue': '#00bfff',
        'dimgray': '#696969',
        'dodgerblue': '#1e90ff',
        'firebrick': '#b22222',
        'floralwhite': '#fffaf0',
        'forestgreen': '#228b22',
        'fuchsia': '#ff00ff',
        'gainsboro': '#dcdcdc',
        'ghostwhite': '#f8f8ff',
        'gold': '#ffd700',
        'goldenrod': '#daa520',
        'gray': '#808080',
        'green': '#008000',
        'greenyellow': '#adff2f',
        'honeydew': '#f0fff0',
        'hotpink': '#ff69b4',
        'indianred ': '#cd5c5c',
        'indigo': '#4b0082',
        'ivory': '#fffff0',
        'khaki': '#f0e68c',
        'lavender': '#e6e6fa',
        'lavenderblush': '#fff0f5',
        'lawngreen': '#7cfc00',
        'lemonchiffon': '#fffacd',
        'lightblue': '#add8e6',
        'lightcoral': '#f08080',
        'lightcyan': '#e0ffff',
        'lightgoldenrodyellow': '#fafad2',
        'lightgrey': '#d3d3d3',
        'lightgreen': '#90ee90',
        'lightpink': '#ffb6c1',
        'lightsalmon': '#ffa07a',
        'lightseagreen': '#20b2aa',
        'lightskyblue': '#87cefa',
        'lightslategray': '#778899',
        'lightsteelblue': '#b0c4de',
        'lightyellow': '#ffffe0',
        'lime': '#00ff00',
        'limegreen': '#32cd32',
        'linen': '#faf0e6',
        'magenta': '#ff00ff',
        'maroon': '#800000',
        'mediumaquamarine': '#66cdaa',
        'mediumblue': '#0000cd',
        'mediumorchid': '#ba55d3',
        'mediumpurple': '#9370d8',
        'mediumseagreen': '#3cb371',
        'mediumslateblue': '#7b68ee',
        'mediumspringgreen': '#00fa9a',
        'mediumturquoise': '#48d1cc',
        'mediumvioletred': '#c71585',
        'midnightblue': '#191970',
        'mintcream': '#f5fffa',
        'mistyrose': '#ffe4e1',
        'moccasin': '#ffe4b5',
        'navajowhite': '#ffdead',
        'navy': '#000080',
        'oldlace': '#fdf5e6',
        'olive': '#808000',
        'olivedrab': '#6b8e23',
        'orange': '#ffa500',
        'orangered': '#ff4500',
        'orchid': '#da70d6',
        'palegoldenrod': '#eee8aa',
        'palegreen': '#98fb98',
        'paleturquoise': '#afeeee',
        'palevioletred': '#d87093',
        'papayawhip': '#ffefd5',
        'peachpuff': '#ffdab9',
        'peru': '#cd853f',
        'pink': '#ffc0cb',
        'plum': '#dda0dd',
        'powderblue': '#b0e0e6',
        'purple': '#800080',
        'rebeccapurple': '#663399',
        'red': '#ff0000',
        'rosybrown': '#bc8f8f',
        'royalblue': '#4169e1',
        'saddlebrown': '#8b4513',
        'salmon': '#fa8072',
        'sandybrown': '#f4a460',
        'seagreen': '#2e8b57',
        'seashell': '#fff5ee',
        'sienna': '#a0522d',
        'silver': '#c0c0c0',
        'skyblue': '#87ceeb',
        'slateblue': '#6a5acd',
        'slategray': '#708090',
        'snow': '#fffafa',
        'springgreen': '#00ff7f',
        'steelblue': '#4682b4',
        'tan': '#d2b48c',
        'teal': '#008080',
        'thistle': '#d8bfd8',
        'tomato': '#ff6347',
        'turquoise': '#40e0d0',
        'violet': '#ee82ee',
        'wheat': '#f5deb3',
        'white': '#ffffff',
        'whitesmoke': '#f5f5f5',
        'yellow': '#ffff00',
        'yellowgreen': '#9acd32'
    };
}
